import { useEffect, useState } from 'react'

const KEY = 'ac_sidebar_collapsed'
const WIDE = '(min-width: 769px)'
export const SIDEBAR_FULL = 248
export const SIDEBAR_RAIL = 64

function readStored() {
  try { return localStorage.getItem(KEY) === '1' } catch { return false }
}

/**
 * Whether the sidebar is folded down to its icon rail.
 *
 * Remembered per browser, and only honoured on a wide screen: on a phone the
 * sidebar is a drawer, which wants its labels whatever was chosen on a desk.
 * The width is published as --sidebar-width on the root, which is what the
 * page's left margin and the sidebar itself both read.
 */
export function useSidebarCollapsed() {
  const [stored, setStored] = useState(readStored)
  const [wide, setWide] = useState(() => typeof window === 'undefined' || !window.matchMedia || window.matchMedia(WIDE).matches)

  useEffect(() => {
    if (!window.matchMedia) return
    const mq = window.matchMedia(WIDE)
    const on = e => setWide(e.matches)
    mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on)
    return () => { mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on) }
  }, [])

  // Another sidebar instance (or tab) changed it.
  useEffect(() => {
    const on = e => { if (!e.key || e.key === KEY) setStored(readStored()) }
    window.addEventListener('storage', on)
    window.addEventListener('ac:sidebar', on)
    return () => { window.removeEventListener('storage', on); window.removeEventListener('ac:sidebar', on) }
  }, [])

  const collapsed = stored && wide

  // "[" folds and unfolds it, as in most tools with a sidebar.
  useEffect(() => {
    function onKey(e) {
      if (e.key !== '[' || e.metaKey || e.ctrlKey || e.altKey) return
      const t = e.target
      if (t && (['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName) || t.isContentEditable)) return
      setCollapsed(!readStored())
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    document.documentElement.style.setProperty('--sidebar-width', `${collapsed ? SIDEBAR_RAIL : SIDEBAR_FULL}px`)
  }, [collapsed])

  function setCollapsed(next) {
    const value = typeof next === 'function' ? next(stored) : next
    try { localStorage.setItem(KEY, value ? '1' : '0') } catch { /* private mode */ }
    setStored(value)
    window.dispatchEvent(new Event('ac:sidebar'))
  }

  return [collapsed, setCollapsed]
}
